import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MvCard from "@/components/feature/MvCard";
import { mvList } from "@/mocks/mv";
import { useSeo } from "@/hooks/useSeo";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [input, setInput] = useState(q);

  useSeo({
    title: q ? `红桃MV视频 - 搜索“${q}”` : "红桃MV视频 - MV搜索",
    description: `在红桃MV视频搜索“${q}”相关的音乐视频，快速找到你想要的 MV 与歌手作品。`,
    keywords: "红桃MV视频,MV搜索,音乐视频搜索,MV名称,歌手",
  });

  useEffect(() => {
    setInput(q);
  }, [q]);

  const results = useMemo(() => {
    const key = q.trim().toLowerCase();
    if (!key) return [];
    return mvList.filter(
      (mv) => mv.title.toLowerCase().includes(key) || mv.artist.toLowerCase().includes(key)
    );
  }, [q]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    setSearchParams(next ? { q: next } : {});
  };

  const hotList = [...mvList].sort((a, b) => b.trending - a.trending).slice(0, 4);

  return (
    <div className="w-full">
      <div className="border-b border-background-800 bg-background-900/50 px-4 py-10 md:px-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">搜索 MV</h1>
        <p className="mt-1 text-sm text-foreground-400">支持按 MV 名称或歌手模糊搜索</p>
        <form onSubmit={submit} className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3 py-2">
          <i className="ri-search-line text-lg text-foreground-400" aria-hidden="true"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入 MV 名称或歌手，如“林知夏”"
            name="q"
            className="w-full bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-500"
          />
          <button type="submit" className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600">
            搜索
          </button>
        </form>
      </div>

      <div className="w-full px-4 py-8 md:px-8">
        {q ? (
          <p className="mb-5 text-sm text-foreground-400">
            关键词“<span className="text-accent-500">{q}</span>”共找到
            <span className="text-accent-500"> {results.length} </span>个结果
          </p>
        ) : (
          <p className="mb-5 text-sm text-foreground-400">输入关键词开始搜索，或看看下面的热门推荐</p>
        )}

        {q && results.length > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {results.map((mv) => (
              <MvCard key={mv.id} mv={mv} />
            ))}
          </div>
        )}

        {q && results.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-800 bg-background-900 py-16 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-500" aria-hidden="true"></i>
            <h3 className="mt-4 text-lg font-medium text-foreground-100">没有找到“{q}”相关的 MV</h3>
            <p className="mt-2 text-sm text-foreground-400">换个关键词试试，或者看看下面的热门 MV</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={() => { setInput(""); setSearchParams({}); }} className="cursor-pointer rounded-md border border-background-700 px-5 py-2.5 text-sm text-foreground-300 hover:border-primary-500 hover:text-primary-400">
                清空搜索
              </button>
              <Link to="/charts" className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
                去看排行榜
              </Link>
            </div>
          </div>
        )}

        {(!q || results.length === 0) && (
          <div className="mt-8">
            <h2 className="mb-4 font-heading text-xl font-bold text-foreground-50">热门 MV 推荐</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {hotList.map((mv) => (
                <MvCard key={mv.id} mv={mv} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}